/* eslint-disable global-require */
import { polyfill } from 'smoothscroll-polyfill';

import React from 'react';
import { hydrate } from 'react-dom';
import { createInstance } from '@optimizely/react-sdk';
import createStore from './store';
import App from './containers/App';

import { TrackingClient } from '@sparefoot/tracking-client';
import { ReactSegmentWrapper } from '@sparefoot/segment-react-wrapper/dist/ReactSegmentWrapper';

polyfill();

/**
 * NOTE: ENVIRONMENT VARIABLES
 *
 * Don't use `process.env` to access environment variables here.
 *
 * To pass in environment variables, use the `Config` library.
 * Examples can be found in `server/config/index.js`
 *
 * To access the environment variables, use `config.foo.bar`
 */

const decodedState = window?.__PRELOADED_STATE__
	? JSON.parse(Buffer.from(window.__PRELOADED_STATE__, 'base64').toString())
	: null;

const store = createStore(decodedState);
const { app, user } = store.getState();

TrackingClient.initialize(
	new ReactSegmentWrapper({
		writeKey: app.segmentWriteKey,
		visitId: user.visitId,
		environment: app.environment,
		siteId: -1,
		cdnURL: app.eventsCdnUrl,
		integrations: {
			'Segment.io': {
				apiHost: app.eventsApiUrl,
			},
		},
		debug: app.environment !== 'production',
	})
);

// Read Optimizely context from server (embedded in HTML)
const datafileFromServer = window.__OPTIMIZELY_DATAFILE__;
const userFromServer = window.__OPTIMIZELY_USER__;

/**
 * Create Optimizely client with server's datafile.
 *
 * IMPORTANT: Only pass datafile - do NOT pass sdkKey here.
 * Adding sdkKey triggers async datafile fetching, which prevents the SDK
 * from being "ready" during hydration, causing a flash of wrong content.
 * Server handles datafile updates; client just uses what server embedded.
 */
let optimizelyClient = null;
if (datafileFromServer) {
	try {
		optimizelyClient = createInstance({
			datafile: datafileFromServer,
		});
	} catch (err) {
		console.error('Optimizely SDK initialization error:', err);
	}
}

const render = (AppComponent) =>
	hydrate(
		<AppComponent
			store={store}
			optimizelyClient={optimizelyClient}
			optimizelyUser={userFromServer}
		/>,
		document.getElementById('js-page-content')
	);

/**
 * Wait for Optimizely SDK to be ready before hydrating.
 *
 * The React SDK wrapper requires setUser() to be called before it's truly
 * ready to make decisions. Without this, useDecision returns defaults during
 * initial render, causing a flash when it later updates to the real decision.
 *
 * Flow:
 * 1. Call setUser() to trigger React SDK's internal initialization
 * 2. Apply forced decisions (must be AFTER setUser - they're user-scoped)
 * 3. Wait for onReady() to resolve
 * 4. Now hydrate - SDK will return correct decisions immediately
 */
(async () => {
	if (optimizelyClient) {
		try {
			// Set user to trigger React SDK initialization
			const userId = userFromServer?.id || store.getState().user.visitId;
			optimizelyClient.setUser({
				id: userId,
				attributes: userFromServer?.attributes,
			});

			// Wait for SDK to be ready (should be fast with datafile already loaded)
			await optimizelyClient.onReady({ timeout: 1000 });

			// Apply forced decisions on the user context (must be after setUser and onReady)
			if (userFromServer?.forcedDecisions?.length > 0) {
				const userContext = optimizelyClient.getUserContext();
				for (const { flagKey, variationKey } of userFromServer.forcedDecisions) {
					try {
						// Set on user context (required for useDecision hook to pick it up)
						if (userContext) {
							userContext.setForcedDecision({ flagKey }, { variationKey });
						} else {
							// Fallback to client level if no user context
							optimizelyClient.setForcedDecision({ flagKey }, { variationKey });
						}
					} catch (err) {
						console.error(`[Optimizely] Failed to set forced decision for ${flagKey}:`, err);
					}
				}
			}
		} catch (err) {
			console.warn('[Optimizely] SDK ready timeout, hydrating anyway:', err);
		}
	}
	render(App);
})();

if (module.hot) {
	module.hot.accept(['./containers/App', './pages/Bundles'], () => {
		const NextApp = require('./containers/App').default;
		render(NextApp);
	});
}
